/**
 * Minimal, strict MessagePack decoder for UNTRUSTED input (client → server frames).
 *
 * Why not msgpackr: its decoder always honours msgpackr's record extension, which compiles a
 * reader with `new Function` per record structure. A hostile 64 KB frame can redefine structures
 * thousands of times and burn ~10–30 ms of server CPU per frame. It also materialises extension
 * types (RegExp, Error, typed arrays …) and recurses until the stack overflows on deep nesting.
 *
 * This decoder accepts only what a ClientMessage can contain — nil, booleans, numbers, UTF-8
 * strings, arrays and string-keyed maps, plus msgpackr's `undefined` (fixext1 type 0) — with a
 * nesting limit, container lengths checked against the remaining bytes, and no trailing garbage.
 * Everything else throws a StrictDecodeError.
 */

export class StrictDecodeError extends Error {
  override name = 'StrictDecodeError';
}

const utf8 = new TextDecoder('utf-8', { fatal: true });

class Reader {
  pos = 0;
  private readonly buf: Uint8Array;
  private readonly maxDepth: number;
  private readonly view: DataView;

  constructor(buf: Uint8Array, maxDepth: number) {
    this.buf = buf;
    this.maxDepth = maxDepth;
    this.view = new DataView(buf.buffer, buf.byteOffset, buf.byteLength);
  }

  private need(n: number): void {
    if (this.pos + n > this.buf.length) throw new StrictDecodeError('Unexpected end of data');
  }

  private u8(): number {
    this.need(1);
    return this.buf[this.pos++];
  }

  private u16(): number {
    this.need(2);
    const v = this.view.getUint16(this.pos);
    this.pos += 2;
    return v;
  }

  private u32(): number {
    this.need(4);
    const v = this.view.getUint32(this.pos);
    this.pos += 4;
    return v;
  }

  private str(len: number): string {
    this.need(len);
    const bytes = this.buf.subarray(this.pos, this.pos + len);
    this.pos += len;
    try {
      return utf8.decode(bytes);
    } catch {
      throw new StrictDecodeError('Invalid UTF-8');
    }
  }

  private int64(signed: boolean): number {
    this.need(8);
    const v = signed ? this.view.getBigInt64(this.pos) : this.view.getBigUint64(this.pos);
    this.pos += 8;
    if (v > BigInt(Number.MAX_SAFE_INTEGER) || v < BigInt(Number.MIN_SAFE_INTEGER)) {
      throw new StrictDecodeError('Integer out of safe range');
    }
    return Number(v);
  }

  private array(len: number, depth: number): unknown[] {
    // Every element needs at least one byte: reject lengths the frame cannot possibly hold
    // before allocating anything.
    if (len > this.buf.length - this.pos) throw new StrictDecodeError('Array length exceeds data');
    const out = new Array<unknown>(len);
    for (let i = 0; i < len; i++) out[i] = this.value(depth + 1);
    return out;
  }

  private map(len: number, depth: number): Record<string, unknown> {
    if (len * 2 > this.buf.length - this.pos) throw new StrictDecodeError('Map length exceeds data');
    const out: Record<string, unknown> = {};
    for (let i = 0; i < len; i++) {
      const key = this.value(depth + 1);
      if (typeof key !== 'string') throw new StrictDecodeError('Map keys must be strings');
      if (key === '__proto__' || Object.hasOwn(out, key)) throw new StrictDecodeError(`Bad map key "${key}"`);
      out[key] = this.value(depth + 1);
    }
    return out;
  }

  value(depth: number): unknown {
    if (depth > this.maxDepth) throw new StrictDecodeError('Nesting too deep');
    const b = this.u8();
    if (b <= 0x7f) return b;
    if (b >= 0xe0) return b - 0x100;
    if (b <= 0x8f) return this.map(b & 0x0f, depth);
    if (b <= 0x9f) return this.array(b & 0x0f, depth);
    if (b <= 0xbf) return this.str(b & 0x1f);
    switch (b) {
      case 0xc0:
        return null;
      case 0xc2:
        return false;
      case 0xc3:
        return true;
      case 0xca: {
        this.need(4);
        const v = this.view.getFloat32(this.pos);
        this.pos += 4;
        return v;
      }
      case 0xcb: {
        this.need(8);
        const v = this.view.getFloat64(this.pos);
        this.pos += 8;
        return v;
      }
      case 0xcc:
        return this.u8();
      case 0xcd:
        return this.u16();
      case 0xce:
        return this.u32();
      case 0xcf:
        return this.int64(false);
      case 0xd0:
        return (this.u8() << 24) >> 24;
      case 0xd1:
        return (this.u16() << 16) >> 16;
      case 0xd2:
        return this.u32() | 0;
      case 0xd3:
        return this.int64(true);
      case 0xd4: {
        // fixext1: only msgpackr's encoding of `undefined` (type 0, one zero byte) is accepted.
        const type = this.u8();
        const data = this.u8();
        if (type === 0 && data === 0) return undefined;
        throw new StrictDecodeError('Extension types are not accepted');
      }
      case 0xd9:
        return this.str(this.u8());
      case 0xda:
        return this.str(this.u16());
      case 0xdb:
        return this.str(this.u32());
      case 0xdc:
        return this.array(this.u16(), depth);
      case 0xdd:
        return this.array(this.u32(), depth);
      case 0xde:
        return this.map(this.u16(), depth);
      case 0xdf:
        return this.map(this.u32(), depth);
      default:
        throw new StrictDecodeError(`Unsupported MessagePack type 0x${b.toString(16)}`);
    }
  }
}

/**
 * Decode one MessagePack value from `buf`. Throws StrictDecodeError on anything outside the
 * accepted subset, on nesting deeper than `maxDepth` containers, or on trailing bytes.
 */
export function decodeStrict(buf: Uint8Array, maxDepth = 8): unknown {
  const reader = new Reader(buf, maxDepth);
  const value = reader.value(0);
  if (reader.pos !== buf.length) throw new StrictDecodeError('Trailing bytes after message');
  return value;
}
